import { useState, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import { FaClock, FaHandshake, FaBan } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AuthService from '../../../services/AuthService';
import ClientEOIService from '../../../services/ClientEOIService';

const useClientEOIList = () => {
    const [eois, setEois] = useState([]);
    const [filteredEois, setFilteredEois] = useState([]);
    const [activeFilter, setActiveFilter] = useState('PENDING');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEOI, setSelectedEOI] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);
    const [isChangeProfessionalModalOpen, setIsChangeProfessionalModalOpen] = useState(false);
    const [eoiToChangeProfessional, setEoiToChangeProfessional] = useState(null);



    const handleOpenChangeProfessionalModal = (eoi) => {
        setEoiToChangeProfessional(eoi);
        setIsChangeProfessionalModalOpen(true);
    };

    const handleCloseChangeProfessionalModal = () => {
        setIsChangeProfessionalModalOpen(false);
        setEoiToChangeProfessional(null);
    };

    const handleProfessionalChanged = (updatedEOI) => {
        // Mise à jour optimiste immédiate (retour visuel instantané)
        setEois(prevEois =>
            prevEois.map(eoi => (eoi.id === updatedEOI.id ? updatedEOI : eoi))
        );

        // Rechargement complet des données depuis le serveur,
        // une fois la modal fermée, pour être sûr d'avoir l'état à jour
        fetchClientEOIs();
    };

    // États pour Lightbox
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        fetchClientEOIs();
    }, []);

    useEffect(() => {
        filterEOIs();
    }, [eois, activeFilter]);

    const fetchClientEOIs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const currentUser = AuthService.getCurrentUser();
            if (!currentUser) {
                throw new Error('Utilisateur non connecté');
            }
            const response = await ClientEOIService.getClientEOIsByClientId(currentUser.id);
            setEois(response);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement des demandes');
            console.error('Error fetching client EOIs:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const filterEOIs = () => {
        if (activeFilter === 'REJECTED') {
            setFilteredEois(eois.filter(eoi =>
                eoi.clientEOIStatus === 'REJECTED' || eoi.clientEOIStatus === 'CANCELLED'
            ));
        } else {
            setFilteredEois(eois.filter(eoi => eoi.clientEOIStatus === activeFilter));
        }
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    const handleCancelRequest = async (eoi) => {
        if (!eoi || !AuthService.getCurrentUser()) return;

        const result = await Swal.fire({
            title: 'Confirmer l\'annulation',
            html: `
                <div class="text-start">
                    <p>Êtes-vous sûr de vouloir annuler cette demande ?</p>
                    <div class="alert alert-warning mt-3">
                        <strong>⚠️ Attention:</strong> Cette action est irréversible.
                    </div>
                    <div class="mt-3">
                        <strong>Détails de la demande:</strong><br/>
                        • Professionnel: ${eoi.professional?.user?.firstName} ${eoi.professional?.user?.lastName}<br/>
                        • Service: ${eoi.serviceName || eoi.professional?.selectedService}<br/>
                        • Statut actuel: ${getStatusText(eoi.clientEOIStatus)}
                    </div>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, annuler la demande',
            cancelButtonText: 'Non, garder la demande',
            customClass: {
                popup: 'swal2-popup-custom'
            }
        });

        if (result.isConfirmed) {
            setCancellingId(eoi.id);
            try {
                const currentUser = AuthService.getCurrentUser();
                const cancelData = {
                    lastModifiedBy: currentUser.username,
                    userRejected: currentUser.username,
                    rejectionReason: `Demande annulée par l'utilisateur: ${currentUser.username}`,
                    clientEOIStatus: "CANCELLED"
                };

                const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, cancelData);
                setEois(eois.map(item => item.id === eoi.id ? updatedEOI : item));

                Swal.fire({
                    title: 'Demande annulée !',
                    text: 'Votre demande a été annulée avec succès.',
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
            } catch (error) {
                console.error('Error cancelling EOI:', error);
                Swal.fire('Erreur !', 'Une erreur est survenue lors de l\'annulation.', 'error');
            } finally {
                setCancellingId(null);
            }
        }
    };

    const handleViewDetails = (eoi) => {
        setSelectedEOI(eoi);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEOI(null);
    };

    const handleUpdate = (updatedEOI) => {
        setEois(eois.map(eoi => eoi.id === updatedEOI.id ? updatedEOI : eoi));
    };

    // ✅ Gestion Lightbox
    const handleOpenLightbox = (images, index = 0) => {
        setLightboxImages(images);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const handleCloseLightbox = () => {
        setLightboxOpen(false);
        setLightboxImages([]);
        setLightboxIndex(0);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning" className="fs-6 py-2 px-3 rounded-pill">
                    <FaClock className="me-1" /> En attente
                </Badge>;
            case 'APPROVED':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaHandshake className="me-1" /> Approuvé
                </Badge>;
            case 'REJECTED':
                return <Badge bg="danger" className="fs-6 py-2 px-3 rounded-pill">
                    <FaBan className="me-1" /> Rejeté
                </Badge>;
            case 'CANCELLED':
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaBan className="me-1" /> Annulé
                </Badge>;
            default:
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">Inconnu</Badge>;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'PENDING': return 'En attente';
            case 'APPROVED': return 'Approuvé';
            case 'REJECTED': return 'Rejeté';
            case 'CANCELLED': return 'Annulé';
            default: return 'Inconnu';
        }
    };

    const formatFrequency = (frequency) => {
        switch (frequency) {
            case 'HEURE': return 'Heure(s)';
            case 'HEBDOMADAIRE': return 'Semaine(s)';
            case 'MENSUEL': return 'Mois';
            default: return frequency || 'Non spécifié';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date inconnue';
        try {
            if (Array.isArray(dateString)) {
                const [year, month, day] = dateString;
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            } else if (dateString.includes('T')) {
                const date = new Date(dateString);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            } else {
                const [datePart] = dateString.split(' ');
                const [day, month, year] = datePart.split('-');
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date invalide';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount) + ' FCFA';
    };

    const getServiceImage = (eoi) => {
        if (eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0) {
            const media = eoi.mediaSubscriptions[0];
            return media.urlPresigned || media.url;
        }
        return "/assets/images/default-service.jpg";
    };

    const getProfessionalProfilePicture = (professional) => {
        if (!professional) return null;
        if (professional.profilePhotoUrlPresigned) {
            return professional.profilePhotoUrlPresigned;
        }
        if (professional.profilePhotoUrl) {
            return professional.profilePhotoUrl;
        }
        if (professional.user?.profilePictureUrl) {
            return professional.user.profilePictureUrl;
        }
        return null;
    };

    const getFilterStats = () => {
        return {
            PENDING: eois.filter(e => e.clientEOIStatus === 'PENDING').length,
            APPROVED: eois.filter(e => e.clientEOIStatus === 'APPROVED').length,
            REJECTED: eois.filter(e =>
                e.clientEOIStatus === 'REJECTED' || e.clientEOIStatus === 'CANCELLED'
            ).length
        };
    };

    const filterStats = getFilterStats();

    return {
        eois,
        filteredEois,
        activeFilter,
        isLoading,
        error,
        selectedEOI,
        isModalOpen,
        cancellingId,
        lightboxOpen,
        lightboxImages,
        lightboxIndex,
        filterStats,
        getStatusBadge,
        getStatusText,
        formatFrequency,
        formatDate,
        formatCurrency,
        getServiceImage,
        getProfessionalProfilePicture,
        handleFilterChange,
        handleCancelRequest,
        handleViewDetails,
        handleUpdate,
        handleCloseModal,
        handleOpenLightbox,
        handleCloseLightbox,
        fetchClientEOIs,
        isChangeProfessionalModalOpen,
        eoiToChangeProfessional,
        handleOpenChangeProfessionalModal,
        handleCloseChangeProfessionalModal,
        handleProfessionalChanged
    };
};

export default useClientEOIList;